import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Bird, Trophy, ShieldCheck, ArrowLeft } from "lucide-react";

export default function ChickenInfo() {
  const [top, setTop] = useState([]);
  useEffect(() => {
    api.get("/games/chicken/leaderboard?limit=20").then((r) => setTop(r.data.top)).catch(() => {});
  }, []);

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <div className="pill pill-gold inline-flex items-center gap-1"><Bird className="w-3.5 h-3.5" /> Chicken Road</div>
          <h1 className="font-display text-3xl sm:text-4xl mt-2">Fairness &amp; leaderboard</h1>
        </div>
        <Link to="/play/chicken" className="btn-ghost text-sm inline-flex items-center gap-2" data-testid="back-to-chicken"><ArrowLeft className="w-4 h-4" /> Back to game</Link>
      </div>

      <section className="vexis-card p-6">
        <div className="pill inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Provably fair</div>
        <h2 className="font-display text-2xl mt-2">How every round is verifiable</h2>
        <ol className="mt-4 space-y-3 text-sm text-slate-300 list-decimal list-inside">
          <li>Before your round, the server generates a random <span className="font-mono text-[color:var(--gold)]">server_seed</span> and shows you only its SHA-256 hash — the outcome is now locked in but hidden.</li>
          <li>You supply a random <span className="font-mono text-[color:var(--gold)]">client_seed</span> that mixes into every step. VEXIS cannot control this value.</li>
          <li>Each lane result is derived deterministically: <span className="font-mono text-[color:var(--gold)]">roll = HMAC-SHA256(server_seed, "client_seed:step")</span> — if the first 32 bits of that hash divided by 0xFFFFFFFF are less than the difficulty's survival probability, the crossing is safe.</li>
          <li>When you cash out or the chicken is hit, the server reveals the original <span className="font-mono text-[color:var(--gold)]">server_seed</span> and its SHA-256 hash still matches the pre-round commit — proving nothing was changed mid-round.</li>
        </ol>
        <div className="mt-6 rounded-xl border border-white/5 p-4 text-xs font-mono text-slate-400">
          <div className="text-slate-500 mb-1">Python verifier (copy &amp; run)</div>
{`import hmac, hashlib
def roll(server_seed, client_seed, step):
    h = hmac.new(server_seed.encode(),
        f"{client_seed}:{step}".encode(),
        hashlib.sha256).hexdigest()
    return int(h[:8], 16) / 0xFFFFFFFF
# roll < survival probability = safe`}
        </div>
      </section>

      <section className="vexis-card overflow-hidden">
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <div className="pill inline-flex items-center gap-1"><Trophy className="w-3.5 h-3.5" /> Leaderboard</div>
          <span className="text-xs text-slate-500 font-mono">longest lane streak</span>
        </div>
        <div className="grid grid-cols-12 px-5 py-3 border-b border-white/5 text-[11px] font-mono uppercase tracking-widest text-slate-500">
          <div className="col-span-1">#</div>
          <div className="col-span-6">Player</div>
          <div className="col-span-2 text-right">Rounds</div>
          <div className="col-span-3 text-right">Best</div>
        </div>
        <ul className="divide-y divide-white/5">
          {top.map((r, i) => (
            <li key={r.user_id} className="grid grid-cols-12 px-5 py-3 items-center text-sm" data-testid={`chicken-lb-row-${i}`}>
              <div className="col-span-1 font-mono text-slate-500">{i + 1}</div>
              <div className="col-span-6 text-slate-200 truncate">{r.name}</div>
              <div className="col-span-2 text-right font-mono text-slate-400">{r.rounds}</div>
              <div className="col-span-3 text-right font-mono text-[color:var(--gold)]">{r.best_steps} lanes · {r.best_payout.toLocaleString()}</div>
            </li>
          ))}
          {top.length === 0 && <li className="px-5 py-6 text-slate-500 text-sm">No runs yet.</li>}
        </ul>
      </section>
    </div>
  );
}
